import { useContacts } from '@/hooks/use-contacts';
import { useExecuteMacro, useMacros } from '@/hooks/use-macros';
import { useCommandPaletteStore } from '@/stores/command-palette.store';
import { useInboxStore } from '@/stores/inbox.store';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
  Kbd,
} from '@ominiflow/ui';
import { useNavigate } from '@tanstack/react-router';
import {
  BarChart2,
  BookOpen,
  Bot,
  Building2,
  CheckCheck,
  ChevronRight,
  Columns2,
  Gavel,
  Globe,
  Heart,
  Inbox,
  Key,
  LayoutDashboard,
  Mail,
  MessageSquare,
  Package,
  Settings,
  Shield,
  Smartphone,
  Star,
  Tag,
  User,
  Users,
  Webhook,
  Zap,
} from 'lucide-react';
import { useEffect, useState } from 'react';

const NAV_ITEMS = [
  { label: 'Inbox', path: '/inbox', icon: Inbox, shortcut: 'G I' },
  { label: 'Contatos', path: '/contacts', icon: Users, shortcut: 'G C' },
  { label: 'Campanhas', path: '/campaigns', icon: Zap, shortcut: 'G K' },
  { label: 'Templates', path: '/templates', icon: MessageSquare, shortcut: 'G T' },
  { label: 'Automação', path: '/automation', icon: Bot, shortcut: 'G A' },
  { label: 'Analytics', path: '/analytics', icon: BarChart2, shortcut: 'G N' },
  { label: 'Marketplace', path: '/marketplace', icon: Package },
  { label: 'Visão ao Vivo', path: '/live-view', icon: LayoutDashboard },
  { label: 'Faturamento', path: '/billing', icon: Building2 },
] as const;

const SETTINGS_ITEMS = [
  { label: 'Configurações Gerais', path: '/settings', icon: Settings },
  { label: 'Agentes', path: '/settings/agents', icon: Users },
  { label: 'Inboxes', path: '/settings/inboxes', icon: Inbox },
  { label: 'Respostas Rápidas', path: '/settings/canned-responses', icon: MessageSquare },
  { label: 'Macros', path: '/settings/macros', icon: Zap },
  { label: 'WhatsApp Oficial', path: '/settings/channels/whatsapp-official', icon: Smartphone },
  {
    label: 'WhatsApp Não-Oficial',
    path: '/settings/channels/whatsapp-unofficial',
    icon: Smartphone,
  },
  { label: 'Telegram', path: '/settings/channels/telegram', icon: Globe },
  { label: 'SMS', path: '/settings/channels/sms', icon: MessageSquare },
  { label: 'E-mail', path: '/settings/channels/email', icon: Mail },
  { label: 'E-mail Inbound', path: '/settings/channels/email-inbound', icon: Mail },
  { label: 'Logs de Auditoria', path: '/settings/audit-logs', icon: Tag },
  { label: 'Base de Conhecimento', path: '/settings/knowledge-base', icon: BookOpen },
  { label: 'Webhooks', path: '/settings/webhooks', icon: Webhook },
  { label: 'API Keys', path: '/settings/api-keys', icon: Key },
  { label: 'Segurança', path: '/settings/security', icon: Shield },
  { label: 'Capacidade de Agentes', path: '/settings/agent-capacity', icon: Columns2 },
  { label: 'Políticas de SLA', path: '/settings/sla', icon: Shield },
  { label: 'Privacidade e LGPD', path: '/settings/privacy', icon: Gavel },
] as const;

const REPORTS_ITEMS = [
  { label: 'Relatório CSAT', path: '/reports/csat', icon: Heart },
  { label: 'Relatório NPS', path: '/reports/nps', icon: Star },
  { label: 'Performance de Agentes', path: '/reports/agent-performance', icon: BarChart2 },
] as const;

export function CommandPalette() {
  const open = useCommandPaletteStore((s) => s.open);
  const setOpen = useCommandPaletteStore((s) => s.setOpen);
  const navigate = useNavigate();
  const selectedTicket = useInboxStore((s) => s.tickets.find((t) => t.id === s.selectedTicketId));
  const activeConversationId = selectedTicket?.conversationId ?? '';
  const [searchQuery, setSearchQuery] = useState('');

  // T2.5 — Macros in command palette
  const { data: macros } = useMacros();
  const executeMacro = useExecuteMacro();

  // T4 — Live contact search when query >= 2 chars
  const { data: contactsResult } = useContacts({
    search: searchQuery.length >= 2 ? searchQuery : undefined,
    pageSize: 5,
  });
  const liveContacts = searchQuery.length >= 2 ? (contactsResult?.data ?? []) : [];

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(true);
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [setOpen]);

  // Reset search when closing
  useEffect(() => {
    if (!open) setSearchQuery('');
  }, [open]);

  function go(path: string) {
    setOpen(false);
    navigate({ to: path });
  }

  function closeTicket() {
    setOpen(false);
    // TODO: T1.1 — integrar com useInboxStore action quando disponível
  }

  return (
    <CommandDialog
      open={open}
      onOpenChange={setOpen}
      title="Command Palette"
      description="Navegue e execute ações rapidamente"
      showCloseButton={false}
      className="max-w-2xl"
    >
      <CommandInput
        placeholder="Navegue, busque, aja..."
        value={searchQuery}
        onValueChange={setSearchQuery}
      />
      <CommandList className="max-h-105">
        <CommandEmpty>
          <output aria-live="polite" className="flex flex-col items-center gap-2 py-6 text-center">
            <span className="text-sm text-muted-foreground">Nenhum resultado encontrado.</span>
            <span className="text-xs text-muted-foreground/60">Tente outro termo de busca.</span>
          </output>
        </CommandEmpty>

        {/* T4 — Live contact search results */}
        {liveContacts.length > 0 && (
          <>
            <CommandGroup heading="Contatos">
              {liveContacts.map((contact) => (
                <CommandItem
                  key={contact.id}
                  onSelect={() => {
                    setOpen(false);
                    navigate({ to: '/contacts', search: { id: contact.id } as never });
                  }}
                  className="flex items-center gap-2"
                >
                  <User className="h-4 w-4 text-muted-foreground" />
                  <div className="flex flex-col min-w-0">
                    <span className="text-sm truncate">
                      {contact.name ?? contact.phone ?? contact.email}
                    </span>
                    {contact.phone && contact.name && (
                      <span className="text-[11px] text-muted-foreground truncate">
                        {contact.phone}
                      </span>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandSeparator />
          </>
        )}

        <CommandGroup heading="Navegação Principal">
          {NAV_ITEMS.map((item) => (
            <CommandItem
              key={item.path}
              onSelect={() => go(item.path)}
              className="flex items-center gap-2"
            >
              <item.icon className="h-4 w-4 text-muted-foreground" />
              <span>{item.label}</span>
              {'shortcut' in item && item.shortcut && (
                <CommandShortcut>{item.shortcut}</CommandShortcut>
              )}
            </CommandItem>
          ))}
        </CommandGroup>

        <CommandSeparator />

        <CommandGroup heading="Relatórios">
          {REPORTS_ITEMS.map((item) => (
            <CommandItem
              key={item.path}
              onSelect={() => go(item.path)}
              className="flex items-center gap-2"
            >
              <item.icon className="h-4 w-4 text-muted-foreground" />
              <span>{item.label}</span>
            </CommandItem>
          ))}
        </CommandGroup>

        <CommandSeparator />

        <CommandGroup heading="Configurações">
          {SETTINGS_ITEMS.map((item) => (
            <CommandItem
              key={item.path}
              onSelect={() => go(item.path)}
              className="flex items-center gap-2"
            >
              <item.icon className="h-4 w-4 text-muted-foreground" />
              <span>{item.label}</span>
            </CommandItem>
          ))}
        </CommandGroup>

        {selectedTicket && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Ação na Conversa Atual">
              <CommandItem onSelect={closeTicket} className="flex items-center gap-2">
                <CheckCheck className="h-4 w-4 text-muted-foreground" />
                <span>Fechar conversa</span>
                <CommandShortcut>Alt+R</CommandShortcut>
              </CommandItem>
              <CommandItem onSelect={() => setOpen(false)} className="flex items-center gap-2">
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
                <span>Ver detalhes do contato</span>
              </CommandItem>
            </CommandGroup>
          </>
        )}

        {/* T2.5 — Macros group (only when a conversation is open) */}
        {selectedTicket && activeConversationId && (macros?.macros ?? []).length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Macros">
              {(macros?.macros ?? []).map((macro) => (
                <CommandItem
                  key={macro.id}
                  onSelect={() => {
                    executeMacro.mutate({
                      macroId: macro.id,
                      conversationId: activeConversationId,
                    });
                    setOpen(false);
                  }}
                  className="flex items-center gap-2"
                >
                  <Zap className="h-4 w-4 text-amber-500" />
                  <span>{macro.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}
      </CommandList>
      {/* Footer with keyboard hints */}
      <div className="flex items-center justify-between border-t border-border px-3 py-2 text-[11px] text-muted-foreground/50">
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1">
            <Kbd>↑↓</Kbd>
            navegar
          </span>
          <span className="flex items-center gap-1">
            <Kbd>↵</Kbd>
            abrir
          </span>
          <span className="flex items-center gap-1">
            <Kbd>Esc</Kbd>
            fechar
          </span>
        </div>
        <span className="opacity-60">⌘K</span>
      </div>
    </CommandDialog>
  );
}
